import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { getFirestoreDb } from '../config/firebaseApp';
import type {
  CommunityEventDoc,
  CreateEventInput,
  CreateListingInput,
  MarketplaceListingDoc,
  MarketplaceListingStatus,
  UpdateListingInput,
} from '../types/community';

const EVENTS = 'events';
const MARKETPLACE = 'marketplace';

function mapEvent(id: string, data: Record<string, unknown>): CommunityEventDoc {
  const raw = data.participants;
  const participants = Array.isArray(raw)
    ? raw.filter((p): p is string => typeof p === 'string')
    : [];
  return {
    id,
    title: String(data.title ?? ''),
    eventAt: data.eventAt as CommunityEventDoc['eventAt'],
    venueName: String(data.venueName ?? ''),
    category: String(data.category ?? ''),
    city: String(data.city ?? ''),
    district: String(data.district ?? ''),
    createdBy: String(data.createdBy ?? ''),
    createdAt: data.createdAt as CommunityEventDoc['createdAt'],
    participants,
  };
}

function mapListing(id: string, data: Record<string, unknown>): MarketplaceListingDoc {
  const rawStatus = data.status;
  const status: MarketplaceListingStatus | undefined =
    rawStatus === 'sold' || rawStatus === 'inactive' || rawStatus === 'active'
      ? rawStatus
      : undefined;
  const rawImgs = data.images;
  let images: string[] | undefined;
  if (Array.isArray(rawImgs)) {
    images = rawImgs.filter((u): u is string => typeof u === 'string' && u.length > 0);
    if (images.length === 0) {
      images = undefined;
    }
  }
  return {
    id,
    title: String(data.title ?? ''),
    description: String(data.description ?? ''),
    category: String(data.category ?? 'Diğer'),
    price: Number(data.price ?? 0),
    isDonation: Boolean(data.isDonation),
    condition: String(data.condition ?? ''),
    city: String(data.city ?? ''),
    district: String(data.district ?? ''),
    sellerId: String(data.sellerId ?? ''),
    createdAt: data.createdAt as MarketplaceListingDoc['createdAt'],
    images,
    status,
    isTradeable: Boolean(data.isTradeable),
  };
}

function isListingActive(row: MarketplaceListingDoc): boolean {
  return row.status !== 'sold' && row.status !== 'inactive';
}

export async function getEvents(city: string, district?: string): Promise<CommunityEventDoc[]> {
  const trimmedCity = city.trim();
  if (!trimmedCity) {
    return [];
  }
  const trimmedDistrict = district?.trim() ?? '';
  const db = getFirestoreDb();
  const q = trimmedDistrict
    ? query(
        collection(db, EVENTS),
        where('city', '==', trimmedCity),
        where('district', '==', trimmedDistrict),
      )
    : query(collection(db, EVENTS), where('city', '==', trimmedCity));
  const snap = await getDocs(q);
  const now = Date.now();
  const list: CommunityEventDoc[] = [];
  snap.forEach(d => {
    list.push(mapEvent(d.id, d.data() as Record<string, unknown>));
  });
  return list
    .filter(ev => {
      try {
        return ev.eventAt.toMillis() >= now;
      } catch {
        return false;
      }
    })
    .sort((a, b) => a.eventAt.toMillis() - b.eventAt.toMillis());
}

export async function createEvent(
  createdBy: string,
  eventData: CreateEventInput,
): Promise<string> {
  const db = getFirestoreDb();
  const ref = await addDoc(collection(db, EVENTS), {
    title: eventData.title.trim(),
    venueName: eventData.venueName.trim(),
    category: eventData.category.trim(),
    city: eventData.city.trim(),
    district: eventData.district.trim(),
    eventAt: Timestamp.fromDate(eventData.eventAt),
    createdBy,
    createdAt: serverTimestamp(),
    participants: [createdBy],
  });
  return ref.id;
}

export async function updateEventDetails(
  eventId: string,
  editorUid: string,
  patch: { title: string; venueName: string; eventAt: Date },
): Promise<void> {
  const db = getFirestoreDb();
  const ref = doc(db, EVENTS, eventId);
  const docSnap = await getDoc(ref);
  if (!docSnap.exists()) {
    throw new Error('Etkinlik bulunamadı.');
  }
  const data = docSnap.data() as Record<string, unknown>;
  if (String(data.createdBy ?? '') !== editorUid) {
    throw new Error('Yalnızca oluşturan düzenleyebilir.');
  }
  await updateDoc(ref, {
    title: patch.title.trim(),
    venueName: patch.venueName.trim(),
    eventAt: Timestamp.fromDate(patch.eventAt),
  });
}

/** Katılımcı listesinden kendi UID’ini çıkar (oluşturan kullanamaz). */
export async function leaveEvent(eventId: string, uid: string): Promise<void> {
  const db = getFirestoreDb();
  const ref = doc(db, EVENTS, eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error('Etkinlik bulunamadı.');
  }
  const data = snap.data() as Record<string, unknown>;
  if (String(data.createdBy ?? '') === uid) {
    throw new Error('Etkinlik oluşturan katılımdan çıkamaz.');
  }
  const raw = data.participants;
  const parts = Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : [];
  if (!parts.includes(uid)) {
    return;
  }
  await updateDoc(ref, { participants: arrayRemove(uid) });
}

/** Katılımcı listesine UID ekle; zaten varsa işlem yapılmaz. */
export async function joinEvent(eventId: string, uid: string): Promise<void> {
  const db = getFirestoreDb();
  const ref = doc(db, EVENTS, eventId);
  await runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) {
      throw new Error('Etkinlik bulunamadı.');
    }
    const data = snap.data() as Record<string, unknown>;
    const raw = data.participants;
    const parts = Array.isArray(raw) ? raw.filter((p): p is string => typeof p === 'string') : [];
    if (parts.includes(uid)) {
      return;
    }
    transaction.update(ref, { participants: arrayUnion(uid) });
  });
}

export async function getMarketplaceListings(
  city: string,
  district?: string,
): Promise<MarketplaceListingDoc[]> {
  const trimmedCity = city.trim();
  if (!trimmedCity) {
    return [];
  }
  const trimmedDistrict = district?.trim() ?? '';
  const db = getFirestoreDb();
  const q = query(collection(db, MARKETPLACE), where('city', '==', trimmedCity));
  const snap = await getDocs(q);
  const list: MarketplaceListingDoc[] = [];
  snap.forEach(d => {
    const row = mapListing(d.id, d.data() as Record<string, unknown>);
    if (!isListingActive(row)) {
      return;
    }
    if (trimmedDistrict && row.district !== trimmedDistrict) {
      return;
    }
    list.push(row);
  });
  return list.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
}

/** Ücretsiz sahiplendirme / bağış ilanları (aktif). */
export async function getDonationListings(
  city: string,
  district?: string,
): Promise<MarketplaceListingDoc[]> {
  const all = await getMarketplaceListings(city, district);
  return all.filter(row => row.isDonation);
}

/** Satıcının tüm ilanları (aktif + pasif); İlanlarım sekmesi. */
export async function getMarketplaceListingsBySeller(
  sellerId: string,
): Promise<MarketplaceListingDoc[]> {
  const db = getFirestoreDb();
  const q = query(collection(db, MARKETPLACE), where('sellerId', '==', sellerId));
  const snap = await getDocs(q);
  const list: MarketplaceListingDoc[] = [];
  snap.forEach(d => {
    list.push(mapListing(d.id, d.data() as Record<string, unknown>));
  });
  return list.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
}

export async function createListing(
  sellerId: string,
  listingData: CreateListingInput,
): Promise<string> {
  const db = getFirestoreDb();
  const priceVal = listingData.isDonation ? 0 : Math.round(Number(listingData.price));
  const ref = await addDoc(collection(db, MARKETPLACE), {
    title: listingData.title.trim(),
    description: listingData.description.trim(),
    category: listingData.category,
    price: priceVal,
    isDonation: listingData.isDonation,
    isTradeable: listingData.isTradeable,
    condition: '',
    city: listingData.city.trim(),
    district: listingData.district.trim(),
    sellerId,
    status: 'active',
    images: listingData.images,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateListing(
  listingId: string,
  sellerId: string,
  data: UpdateListingInput,
): Promise<void> {
  const db = getFirestoreDb();
  const ref = doc(db, MARKETPLACE, listingId);
  const snap = await getDoc(ref);
  if (!snap.exists() || String(snap.data()?.sellerId ?? '') !== sellerId) {
    throw new Error('İlan bulunamadı veya yetkin yok.');
  }
  const priceVal = data.isDonation ? 0 : Math.round(Number(data.price));
  await updateDoc(ref, {
    title: data.title.trim(),
    description: data.description.trim(),
    category: data.category,
    price: priceVal,
    isDonation: data.isDonation,
    isTradeable: data.isTradeable,
  });
}

export async function setListingInactive(listingId: string, sellerId: string): Promise<void> {
  const db = getFirestoreDb();
  const ref = doc(db, MARKETPLACE, listingId);
  const snap = await getDoc(ref);
  if (!snap.exists() || String(snap.data()?.sellerId ?? '') !== sellerId) {
    throw new Error('İlan bulunamadı veya yetkin yok.');
  }
  await updateDoc(ref, { status: 'inactive' });
}
